import { useEffect, useMemo, useRef } from "react";
import {
  useCompanyFiltersStore,
  useCompanyFiltersStoreApi,
} from "../../provider/CompanyFilterProvider";
import { useGetIntentTopicsByIdsLazyQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import type { CachedTopic } from "./slice";

/**
 * Hydrates Intent topic display names into topicsCache when topicIds are present.
 *
 * This is called from the controller so chips and the Selected tab can show names
 * even if the Intent filter panel was never opened.
 */
export function useIntentTopicsHydration(): void {
  const slice = useCompanyFiltersStore((s) => s.slices.intent);
  const storeApi = useCompanyFiltersStoreApi();

  const [fetchTopicsByIds] = useGetIntentTopicsByIdsLazyQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });

  const cachedIds = useMemo(
    () => new Set(slice.topicsCache.map((t) => t.id)),
    [slice.topicsCache]
  );

  // Track in-flight request to avoid duplicates and handle stale responses
  const requestRef = useRef<string | null>(null);
  const attemptedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const idsToFetch = slice.topicIds.filter(
      (id) => !cachedIds.has(id) && !attemptedIdsRef.current.has(id)
    );

    if (idsToFetch.length === 0) return;

    const requestKey = idsToFetch.join(",");
    if (requestRef.current === requestKey) return;
    requestRef.current = requestKey;

    fetchTopicsByIds({ variables: { ids: idsToFetch } })
      .then(({ data }) => {
        if (requestRef.current !== requestKey) return;

        const topicsFromApi =
          data?.intentTopic?.data?.filter(
            (topic): topic is NonNullable<typeof topic> =>
              topic !== null && topic.id !== null && topic.displayName !== null
          ) ?? [];

        if (topicsFromApi.length === 0) return;

        const returnedIds = new Set<string>(
          topicsFromApi.map((t) => t.id as string)
        );
        const stillMissingIds = idsToFetch.filter((id) => !returnedIds.has(id));
        stillMissingIds.forEach((id) => attemptedIdsRef.current.add(id));

        const hydratedTopics: CachedTopic[] = topicsFromApi.map((topic) => ({
          id: topic.id as string,
          displayName: topic.displayName as string,
          category: topic.category ?? undefined,
          collectionTree: (topic.collectionTree ?? []).filter(
            (t): t is string => t !== null
          ),
        }));

        const latestSlice = storeApi.getState().slices.intent;
        const latestCachedIds = new Set(
          latestSlice.topicsCache.map((t) => t.id)
        );
        const newTopics = hydratedTopics.filter(
          (t) => !latestCachedIds.has(t.id)
        );
        if (newTopics.length === 0) return;

        storeApi.getState().setSlice("intent", {
          ...latestSlice,
          topicsCache: [...latestSlice.topicsCache, ...newTopics],
        });
      })
      .catch(() => {
        // Silently handle errors - hydration will retry on next render
      })
      .finally(() => {
        if (requestRef.current === requestKey) {
          requestRef.current = null;
        }
      });
  }, [cachedIds, fetchTopicsByIds, slice.topicIds, storeApi]);
}
